/**
 * Registers the offline-cache service worker (public/sw.js) in production
 * builds only — running it under `vite dev` would cache dev-server responses
 * (unbundled modules, HMR payloads) and fight Vite's own caching.
 *
 * Doubles as this template's worked example of the non-hook logging pattern
 * (logging-strategy POLICY.md §1): a module-level helper, nowhere near a React
 * tree, that builds its own `Logger` with `createClientLogger()` and emits one
 * wide event per operation — `infra.sw.registered` or `infra.sw.error`, never
 * both, never a progress sequence.
 */
import { registerServiceWorker as register } from "@bindery/ui/lib/register-service-worker";
import { createClientLogger, toError } from "./logging";
import { SERVICE_WORKER_POLICY } from "./security-headers";

/** The message public/sw.js adopts a page's files from; same string there. */
export const LOADED_MESSAGE = "retells:loaded";

export function registerServiceWorker(): void {
  register({
    url: "/sw.js",
    enabled: import.meta.env.PROD,
    trustedTypesPolicy: SERVICE_WORKER_POLICY,
    onLoad: (container) => offerLoadedFiles(container, location),
    log: () => {
      const log = createClientLogger();
      return {
        event: (name, fields) => log.event(name, fields),
        error: (name, cause, fields) =>
          log.error(
            name,
            toError(cause, "service worker registration failed"),
            fields,
          ),
      };
    },
  });
}

/**
 * Hands the worker the files this page loaded before the worker controlled
 * it, once it does.
 *
 * A page loaded with no worker in control — every first visit — fetches its
 * chunks and its chapter before sw.js installs, so none of them pass through
 * its fetch handler, and the install offer's "pages you have already visited
 * stay readable offline" was false for exactly the chapter that brought the
 * reader here. When the worker claims the page, this posts the path the page
 * was loaded at and every same-origin resource URL the page requested before
 * the claim, including any that finish after it. A page already controlled at load sent everything
 * through the worker and posts nothing.
 */
export function offerLoadedFiles(
  container: ServiceWorkerContainer,
  page: Pick<Location, "origin" | "pathname">,
): void {
  if (container.controller !== null) return;
  // Read now, on `load`: by the time the worker claims the page, in-app
  // navigation may have moved the URL off the document that was loaded.
  const landing = page.pathname;
  container.addEventListener(
    "controllerchange",
    () => {
      const worker = container.controller;
      if (worker === null) return;
      const claimedAt = performance.now();
      let posted = false;
      // Not disconnected after the first batch: a request that started
      // before the claim can finish after it, and only this sees it. What
      // started after the claim went through the worker's fetch handler and
      // is dropped, so nothing is posted twice.
      new PerformanceObserver((list) => {
        const earlier = list
          .getEntries()
          .filter((entry) => entry.startTime < claimedAt);
        const urls = sameOriginUrls(earlier, page.origin);
        if (posted && urls.length === 0) return;
        posted = true;
        worker.postMessage({ type: LOADED_MESSAGE, page: landing, urls });
      }).observe({ type: "resource", buffered: true });
    },
    { once: true },
  );
}

function sameOriginUrls(
  entries: readonly PerformanceEntry[],
  origin: string,
): string[] {
  const prefix = `${origin}/`;
  return [
    ...new Set(
      entries
        .map((entry) => entry.name)
        .filter((url) => url.startsWith(prefix)),
    ),
  ];
}
